import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  TrashIcon,
  PencilIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { useVideoGallery } from '../../contexts/VideoGalleryContext';

const VideoGalleryList: React.FC = () => {
  const { videos, deleteVideo } = useVideoGallery();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredVideos = videos.filter(video => {
    const matchesSearch = searchTerm === '' ||
      video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (video.description && video.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const handleDelete = async (id: number) => {
    if (window.confirm('정말로 이 영상을 삭제하시겠습니까?')) {
      try {
        await deleteVideo(id);
        alert('영상이 삭제되었습니다.');
      } catch {
        alert('영상 삭제에 실패했습니다.');
      }
    }
  };

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">영상 갤러리 관리</h1>
          <p className="text-gray-600">홈페이지에 표시되는 YouTube 영상을 관리합니다.</p>
        </div>
        <Link
          to="/admin/video-gallery/new"
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          새 영상 등록
        </Link>
      </div>

      {/* 검색 */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="제목 또는 설명으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      {/* 통계 */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-sm text-gray-600">전체</p>
            <p className="text-2xl font-bold text-gray-800">{videos.length}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">공개</p>
            <p className="text-2xl font-bold text-green-600">
              {videos.filter(v => v.is_published).length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600">Shorts</p>
            <p className="text-2xl font-bold text-blue-600">
              {videos.filter(v => v.is_short).length}
            </p>
          </div>
        </div>
      </div>

      {/* 영상 그리드 */}
      <div className="bg-white rounded-lg shadow p-6">
        {filteredVideos.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">등록된 영상이 없습니다.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredVideos.map((video) => (
              <div
                key={video.id}
                className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all"
              >
                {/* 썸네일 */}
                <div className="relative h-48 bg-gray-900 overflow-hidden">
                  <img
                    src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  {/* 재생 아이콘 오버레이 */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 bg-black/60 rounded-full flex items-center justify-center">
                      <PlayIcon className="w-5 h-5 text-white ml-0.5" />
                    </div>
                  </div>
                  {/* Shorts 배지 */}
                  {video.is_short && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded">
                      Shorts
                    </span>
                  )}
                  {/* 비공개 배지 */}
                  {!video.is_published && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 bg-gray-700 text-white text-xs rounded">
                      비공개
                    </span>
                  )}
                </div>

                {/* 내용 */}
                <div className="p-4">
                  <h3 className="text-sm font-bold text-gray-800 mb-2 line-clamp-1">
                    {video.title}
                  </h3>

                  {video.description && (
                    <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                      {video.description}
                    </p>
                  )}

                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span>순서: {video.display_order}</span>
                    <span>ID: {video.youtube_id.substring(0, 8)}...</span>
                  </div>

                  {/* 액션 버튼 */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/admin/video-gallery/edit/${video.id}`)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" />
                      수정
                    </button>
                    <button
                      onClick={() => handleDelete(video.id)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoGalleryList;
